import type { QueryClient } from '@tanstack/react-query';
import { redirect, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import type { Me } from '@myrewards/contracts';
import { isApiError, waitingQuery } from '../api';
import {
  adminConsoleQuery,
  adminPeopleQuery,
  adminPersonQuery,
  adminPreviewQuery,
  adminRolesQuery,
  auditEntryQuery,
  auditLogQuery,
  discrepanciesQuery,
  goLiveQuery,
  rolloverQuery,
  personAccessQuery,
  syncStatusQuery,
} from '../api/admin';
import { sentMailDetailQuery, sentMailQuery } from '../api/notifications';
import {
  DISPLAY_ID,
  feedbackDetailQuery,
  feedbackKeys,
  feedbackListQuery,
  feedbackSettingsQuery,
} from '../api/feedbackAdmin';
import { notificationEmailsQuery, settingQuery } from '../api/settings';
import { startingSetupQuery } from '../api/starting-setup';
import { noAccess, requirePage } from '../app/access';
import type { RouteHandle } from '../app/RootLayout';
import { idFrom, notFound, orNotFound } from '../ops/routes';
import { forRoles } from '../search/pages';
import { auditFiltersFrom } from './audit/filters';
import { filtersFrom as feedbackFiltersFrom } from './feedback/filters';
import { filtersFrom } from './people/filters';
import { statusFromParam } from './presenter';
import { SentMailDetailPage } from './SentMailDetailPage';
import { SentMailPage } from './SentMailPage';
import { canUseSystemDiagnostics } from './health/access';
import { skipRevalidationFor } from '../ui/revalidate';
import { loaderPaging } from '../ui/usePaging';

/**
 * A list paged on the server: once a new rows per page is saved, the size leaves the address and
 * the loader would read the same page again, so it doesn't run (APP-135 §7).
 */
const sizeSaved = skipRevalidationFor([], { sizeRemoved: ['size'] });
/**
 * A CSV file's preview rows page on the screen on their own params (APP-135 §4). On Directory
 * sync, `page` and `size` are the issues', paged on the server, so they still run the loader.
 */
const importPagedOnScreen = skipRevalidationFor(['importPage', 'importSize']);
const directoryPaging = skipRevalidationFor(['importPage', 'importSize'], {
  sizeRemoved: ['size'],
});

const sentMailHandle: RouteHandle = {
  help: { pageLabel: 'Settings · System administration · Sent mail', topicIds: ['waiting'] },
};

const sentMailListHandle: RouteHandle = {
  ...sentMailHandle,
  search: {
    label: 'Sent mail',
    meta: 'Every email the app sent, with where it went',
    visible: forRoles('system_admin'),
  },
};

const peopleHandle: RouteHandle = {
  help: { pageLabel: 'Settings · System administration · People', topicIds: ['directory-people'] },
  search: {
    label: 'People',
    meta: 'Settings › System administration › People · everyone MyRewards knows about, with their roles',
    visible: forRoles('system_admin'),
  },
};

const auditHandle: RouteHandle = {
  help: { pageLabel: 'Settings · System administration · Audit log', topicIds: ['audit-log'] },
  search: {
    label: 'Audit log',
    meta: 'Settings › System administration › Audit log · every change, who made it and why',
    visible: forRoles('system_admin'),
  },
};

const rolesHandle: RouteHandle = {
  help: {
    pageLabel: 'Settings · System administration · Roles & lists',
    topicIds: ['directory-people', 'requesters'],
  },
  search: {
    label: 'Roles & lists',
    meta: 'Settings › System administration › Roles & lists · Executives, Sector Leads and who holds each role',
    visible: forRoles('system_admin'),
  },
};

const syncSettingsHandle: RouteHandle = {
  help: {
    pageLabel: 'Settings · System administration · Directory sync · Sync settings',
    topicIds: ['directory-people'],
  },
  search: {
    label: 'Sync settings',
    meta: 'Settings › System administration › Directory sync › turn the Entra ID sync on or off, profiles at first sign-in',
    visible: forRoles('system_admin'),
  },
};

const directoryHandle: RouteHandle = {
  help: {
    pageLabel: 'Settings · System administration · Directory sync',
    topicIds: ['directory-people'],
  },
  search: {
    label: 'Directory sync',
    meta: 'Settings › System administration › Directory sync · Entra ID, setup, overrides and CSV imports',
    visible: forRoles('system_admin'),
  },
};

const typesHandle: RouteHandle = {
  help: {
    pageLabel: 'Settings · System administration · Award types & features',
    topicIds: ['award-types', 'spot'],
  },
  search: {
    label: 'Award types & features',
    meta: 'Settings › System administration › Award types, the sign-in screen, the policy link and app-wide switches',
    visible: forRoles('system_admin'),
  },
};

const notificationsHandle: RouteHandle = {
  help: {
    pageLabel: 'Settings · System administration · Notifications',
    topicIds: ['waiting', 'preferences'],
  },
  search: {
    label: 'Notifications',
    meta: 'Settings › System administration › which emails MyRewards sends, the Stock Admin daily digest and the queue’s emails',
    visible: forRoles('system_admin'),
  },
};

const previewHandle: RouteHandle = {
  help: {
    pageLabel: 'Settings · System administration · Preview & testing',
    topicIds: ['preview-as'],
  },
  search: {
    label: 'Preview & testing',
    meta: 'Settings › System administration › Preview & testing · who can use Preview as, and what reaches whom',
    visible: forRoles('system_admin'),
  },
};

/** The settings Award types & features reads first; the rest load as their cards open. */
const TYPE_SETTINGS: readonly [string, string][] = [
  ['spot.enabled', 'system'],
  ['system.splash', 'system'],
  ['org.awardsPolicyUrl', 'system'],
  ['awards.enabled', 'SDB'],
  ['awards.enabled', 'EDB'],
  ['awards.enabled', 'PAA'],
  ['awards.enabled', 'TAA'],
];

const consoleHandle: RouteHandle = {
  help: { pageLabel: 'System overview', topicIds: ['directory-people', 'audit-log', 'preview-as'] },
  search: {
    label: 'System overview',
    meta: 'Settings › System administration › the directory, settings areas, support tools and go-live',
    visible: forRoles('system_admin'),
  },
};

const goLiveHandle: RouteHandle = {
  help: {
    pageLabel: 'Settings · System administration · Go-live checklist',
    topicIds: ['directory-people'],
  },
  search: {
    label: 'Go-live checklist',
    meta: 'Settings › System administration › the 12 steps before MyRewards opens to everyone',
    visible: forRoles('system_admin'),
  },
};

const importsHandle: RouteHandle = {
  help: {
    pageLabel: 'Settings · System administration · CSV imports',
    topicIds: ['directory-people'],
  },
  search: {
    label: 'CSV imports',
    meta: 'Settings › System administration › directory fields, programs and past awards',
    visible: forRoles('system_admin'),
  },
};

const rolloverHandle: RouteHandle = {
  help: {
    pageLabel: 'Settings · System administration · Year rollover',
    topicIds: ['budget-ledger'],
  },
  search: {
    label: 'Year rollover',
    meta: 'Settings › System administration › next year’s budgets draft, the settings review and the archive',
    visible: forRoles('system_admin'),
  },
};

// APP-104 §6: Feedback is open to System Admins and Support, and to nobody else.
const feedbackHandle: RouteHandle = {
  help: { pageLabel: 'Settings · System administration · Feedback', topicIds: ['waiting'] },
};

const feedbackListHandle: RouteHandle = {
  ...feedbackHandle,
  search: {
    label: 'Feedback',
    meta: 'Settings › System administration › Feedback · what people sent with the Feedback button',
    visible: forRoles('system_admin', 'support'),
  },
};

/** System Admins only; anyone else is told it's theirs (APP-134 §5). */
export async function requireSystemAdmin(queryClient: QueryClient, request: Request): Promise<Me> {
  return requirePage(queryClient, request, ['system_admin'], 'System Admins');
}

/** System Admins and Support; anyone else is told it's theirs. */
export async function requireFeedbackReviewer(
  queryClient: QueryClient,
  request: Request,
): Promise<Me> {
  return requirePage(
    queryClient,
    request,
    ['system_admin', 'support'],
    'System Admins and Support',
  );
}

/**
 * An Admin page's loader: System Admins only; while previewing nothing live is read (the page
 * explains); an Admin read the server refuses is No access, and one it can't find is not found.
 */
export async function adminLoad(
  queryClient: QueryClient,
  request: Request,
  load: () => Promise<unknown>,
): Promise<null> {
  const me = await requireSystemAdmin(queryClient, request);
  if (me.preview !== null) return null;
  try {
    await load();
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router's 403 response
    if (isApiError(error) && error.status === 403) throw noAccess('System Admins', error);
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router's 404 response
    if (isApiError(error) && error.status === 404) throw notFound();
    throw error;
  }
  return null;
}

/**
 * The Admin area (SYS1–SYS8). Children of the signed-in layout; each page's code loads on first
 * use. Settings › System administration › AI has its own entry (`aiAdminRoutes`) and the same frame.
 */
export function adminRoutes(queryClient: QueryClient): RouteObject[] {
  return [
    ...(['health', 'debug'] as const).map((page): RouteObject => ({
      path: `settings/system/${page}`,
      // The page owns loading/error states. No diagnostics are fetched by a Preview loader.
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () => Promise.resolve()),
      lazy: async () => ({
        Component:
          page === 'health'
            ? (await import('./health/SystemHealthPage')).SystemHealthPage
            : (await import('./health/SystemDebugPage')).SystemDebugPage,
      }),
      handle: {
        help: {
          pageLabel: `Settings · System administration · System ${page}`,
          topicIds: ['directory-people'],
        },
        search: {
          label: `System ${page}`,
          meta:
            page === 'health'
              ? 'Dependency checks, evidence freshness and next actions'
              : 'Safe deployment inputs and saved system settings',
          visible: canUseSystemDiagnostics,
        },
      } satisfies RouteHandle,
    })),
    {
      path: 'settings/system/organization',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () => Promise.resolve()),
      lazy: async () => ({
        Component: (await import('../settings/OrganizationPage')).OrganizationPage,
      }),
      handle: {
        help: {
          pageLabel: 'Settings · System administration · Organization',
          topicIds: ['directory-people'],
        },
        search: {
          label: 'Organization',
          meta: 'Time zone, fiscal year and custom directory fields',
          visible: forRoles('system_admin'),
        },
      } satisfies RouteHandle,
    },
    {
      // APP-220: Spot Award setup moved to Award setup (/settings/spot); old links land there.
      path: 'settings/system/spot',
      loader: () => {
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router loaders redirect by throwing a Response.
        throw redirect('/settings/spot');
      },
    },
    {
      // Admin opens on its console (SYS1), whatever else the person's roles add.
      path: 'settings/system',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () =>
          Promise.all([
            queryClient.query(adminConsoleQuery),
            queryClient.query(goLiveQuery),
            queryClient.query(waitingQuery),
          ]),
        ),
      lazy: async () => ({ Component: (await import('./console/ConsolePage')).ConsolePage }),
      handle: consoleHandle,
    },
    {
      path: 'settings/system/go-live',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () =>
          Promise.all([
            queryClient.query(goLiveQuery),
            // The card shows its own refusal, so the checklist opens whatever it says.
            queryClient.query(startingSetupQuery).catch(() => null),
            queryClient.query(settingQuery('spot.enabled')).catch(() => null),
            queryClient.query(settingQuery('notifications.events')).catch(() => null),
          ]),
        ),
      lazy: async () => ({ Component: (await import('./golive/GoLivePage')).GoLivePage }),
      handle: goLiveHandle,
    },
    {
      path: 'settings/system/imports',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () => Promise.resolve()),
      shouldRevalidate: importPagedOnScreen,
      lazy: async () => ({ Component: (await import('./imports/ImportsPage')).ImportsPage }),
      handle: importsHandle,
    },
    {
      path: 'settings/system/rollover',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () => queryClient.query(rolloverQuery)),
      lazy: async () => ({ Component: (await import('./rollover/RolloverPage')).RolloverPage }),
      handle: rolloverHandle,
    },
    {
      path: 'settings/system/people',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, async () => {
          const params = new URL(request.url).searchParams;
          const person = params.get('person');
          const paging = await loaderPaging(queryClient, params, 'people');
          await Promise.all([
            queryClient.query(adminConsoleQuery),
            queryClient.query(adminPeopleQuery(filtersFrom(params), paging)),
            ...(person === null
              ? []
              : [queryClient.query(adminPersonQuery(person)).catch(() => null)]),
          ]);
        }),
      shouldRevalidate: sizeSaved,
      lazy: async () => ({ Component: (await import('./people/PeoplePage')).PeoplePage }),
      handle: peopleHandle,
    },
    {
      path: 'settings/system/directory',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, async () => {
          const params = new URL(request.url).searchParams;
          const person = params.get('person');
          const paging = await loaderPaging(queryClient, params, 'directory_issues');
          await Promise.all([
            queryClient.query(adminConsoleQuery),
            queryClient.query(syncStatusQuery),
            queryClient.query(discrepanciesQuery(paging)),
            ...(person === null
              ? []
              : [queryClient.query(adminPersonQuery(person)).catch(() => null)]),
          ]);
        }),
      shouldRevalidate: directoryPaging,
      lazy: async () => ({ Component: (await import('./directory/DirectoryPage')).DirectoryPage }),
      handle: directoryHandle,
    },
    {
      path: 'settings/system/directory/sync',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () =>
          Promise.all([
            queryClient.query(syncStatusQuery),
            ...[
              'directory.sync',
              'directory.syncFilters',
              'directory.matching',
              'directory.firstSignIn',
            ].map((key) => queryClient.query(settingQuery(key)).catch(() => null)),
          ]),
        ),
      lazy: async () => ({
        Component: (await import('./directory/SyncSettingsPage')).SyncSettingsPage,
      }),
      handle: syncSettingsHandle,
    },
    {
      path: 'settings/system/types',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () =>
          Promise.all(
            TYPE_SETTINGS.map(([key, scope]) =>
              queryClient.query(settingQuery(key, scope)).catch(() => null),
            ),
          ),
        ),
      lazy: async () => ({ Component: (await import('./types/AwardTypesPage')).AwardTypesPage }),
      handle: typesHandle,
    },
    {
      path: 'settings/system/notifications',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () =>
          Promise.all([
            queryClient.query(notificationEmailsQuery),
            queryClient.query(settingQuery('notifications.events')).catch(() => null),
            queryClient.query(settingQuery('notifications.stockAdminDigest')).catch(() => null),
          ]),
        ),
      lazy: async () => ({
        Component: (await import('./notifications/NotificationEmailsPage')).NotificationEmailsPage,
      }),
      handle: notificationsHandle,
    },
    {
      path: 'settings/system/preview',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () =>
          Promise.all([
            queryClient.query(adminPreviewQuery),
            queryClient.query(settingQuery('preview.access')).catch(() => null),
          ]),
        ),
      lazy: async () => ({ Component: (await import('./preview/PreviewPage')).PreviewPage }),
      handle: previewHandle,
    },
    {
      path: 'settings/system/roles',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, async () => {
          const person = new URL(request.url).searchParams.get('person');
          await Promise.all([
            queryClient.query(adminRolesQuery),
            ...(person === null
              ? []
              : [queryClient.query(personAccessQuery(person)).catch(() => null)]),
          ]);
        }),
      lazy: async () => ({ Component: (await import('./roles/RolesPage')).RolesPage }),
      handle: rolesHandle,
    },
    {
      path: 'settings/system/audit-log',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, async () => {
          const params = new URL(request.url).searchParams;
          const entry = params.get('entry');
          const paging = await loaderPaging(queryClient, params, 'audit_log');
          const upTo = Number(params.get('upTo'));
          await Promise.all([
            queryClient.query(
              auditLogQuery(auditFiltersFrom(params), {
                ...paging,
                ...(paging.page > 1 && Number.isInteger(upTo) && upTo >= 1 ? { upTo } : {}),
              }),
            ),
            ...(entry === null
              ? []
              : [queryClient.query(auditEntryQuery(entry)).catch(() => null)]),
          ]);
        }),
      shouldRevalidate: sizeSaved,
      lazy: async () => ({ Component: (await import('./audit/AuditLogPage')).AuditLogPage }),
      handle: auditHandle,
    },
    {
      path: 'settings/system/sent-mail',
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireSystemAdmin(queryClient, request);
        const params = new URL(request.url).searchParams;
        const status = statusFromParam(params.get('status'));
        const paging = await loaderPaging(queryClient, params, 'sent_mail');
        await orNotFound(queryClient.query(sentMailQuery(status, paging)));
        return null;
      },
      shouldRevalidate: sizeSaved,
      Component: SentMailPage,
      handle: sentMailListHandle,
    },
    {
      path: 'settings/system/sent-mail/:id',
      loader: async ({ request, params }: LoaderFunctionArgs) => {
        await requireSystemAdmin(queryClient, request);
        const id = idFrom(params);
        await orNotFound(queryClient.query(sentMailDetailQuery(id)));
        return null;
      },
      Component: SentMailDetailPage,
      handle: sentMailHandle,
    },
    {
      path: 'settings/system/feedback',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireFeedbackReviewer(queryClient, request);
        // Nothing live is read while previewing; the shell shows the page's sample layout.
        if (me.preview !== null) return null;
        const params = new URL(request.url).searchParams;
        const filters = feedbackFiltersFrom(params);
        const paging = await loaderPaging(queryClient, params, 'feedback');
        await orNotFound(
          Promise.all([
            queryClient.query(feedbackListQuery(filters, paging)),
            queryClient.query(feedbackSettingsQuery),
          ]),
        );
        return null;
      },
      shouldRevalidate: sizeSaved,
      lazy: async () => ({
        Component: (await import('./feedback/FeedbackListPage')).FeedbackListPage,
      }),
      handle: feedbackListHandle,
    },
    {
      path: 'settings/system/feedback/:displayId',
      loader: async ({ request, params }: LoaderFunctionArgs) => {
        const me = await requireFeedbackReviewer(queryClient, request);
        const displayId = params['displayId'] ?? '';
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router's 404 response
        if (!DISPLAY_ID.test(displayId)) throw notFound();
        if (me.preview !== null) return null;
        // Opening it reads the sender's replies for the whole team (APP-138 §D11): the list's
        // Replies to read and Home's row follow.
        const report = await orNotFound(queryClient.query(feedbackDetailQuery(displayId)));
        if (report.toRead > 0) {
          void queryClient.invalidateQueries({ queryKey: [...feedbackKeys.all, 'list'] });
          void queryClient.invalidateQueries({ queryKey: waitingQuery.queryKey });
        }
        return null;
      },
      lazy: async () => ({
        Component: (await import('./feedback/FeedbackDetailPage')).FeedbackDetailPage,
      }),
      handle: feedbackHandle,
    },
  ];
}
